// src/context/brandVoice/widgets/ClaimsEditor.jsx — the `claims` editor: `verified_claims`, the ONLY section the
// fact-checker treats as established truth.
//
// ⭐ v200: FOUR AREAS, each a named <section> (a region), its categories below as ClaimTable groups on one grid. An
// area of several categories shows their names; an area of one shows its own title. No header rows: every column is
// named inside its placeholder ("Etichetta*: es. …"):
//   Metriche · Risultati con i clienti (Case study) · Credenziali e clienti (Credenziali, Clienti) · Dati qualitativi
//   (last).
// The columns are each category's `required` + `optional` keys from the payload's `claim_fields` (⛔ never
// hard-coded), minus `as_of` — never shown, yet KEPT on every edit (ClaimTable spreads the row), so a date the
// operator onboarding wrote still reaches the prompts. Required columns are starred in the placeholder. A category
// `claim_fields` does not declare is not shown, and an area left with none is not drawn; a category we do not know
// gets its own area after the four, under its raw key. ⭐ Each category receives ONLY its own problems.
// ⛔ It hands up the WHOLE object; which categories are SENT is `draftModel.changedClaims`' job.
import React from 'react';
import { useTranslation } from 'react-i18next';
import ClaimTable from './ClaimTable.jsx';
import { COLORS, FONT_SANS } from '../../../constants/theme.js';

// An area's title is `brandVoice.claimArea.<key>`, or — when it has none — its one category's name.
export const CLAIM_AREAS = [
  { key: 'metrics', categories: ['metrics'] },
  { key: 'cases', categories: ['case_studies'] },
  { key: 'recognition', categories: ['credentials', 'clients'] },
  { key: 'facts', categories: ['facts'] },
];
export const CLAIMS_ORDER = CLAIM_AREAS.flatMap((a) => a.categories);
/** Columns the editor never draws (the value is kept on every edit). */
const NEVER_SHOWN = ['as_of'];
/** The remove and add buttons' columns (REMOVE / ADD_ICON in listStyles are 46px square). */
const ACTION_COLUMNS = ['46px', '46px'];

const isObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const AREA = { display: 'flex', flexDirection: 'column', gap: 12 };
const TITLE = { margin: 0, fontSize: 14, fontWeight: 700, color: COLORS.ink };

const keysOf = (s) => [...(s.required || []), ...(s.optional || [])].filter((k) => !NEVER_SHOWN.includes(k));
const union = (lists) => lists.reduce((acc, list) => [...acc, ...list.filter((k) => !acc.includes(k))], []);

export default function ClaimsEditor({ field, value, onChange, disabled = false, problems = [] }) {
  const { t } = useTranslation('context');
  const spec = isObject(field?.claim_fields) ? field.claim_fields : {};
  const current = isObject(value) ? value : {};
  const labelOf = (c) => t(`brandVoice.claimCategory.${c}`, { defaultValue: c });
  const unknown = Object.keys(spec).filter((c) => !CLAIMS_ORDER.includes(c) && isObject(spec[c]));
  const areas = [
    ...CLAIM_AREAS.map((a) => ({
      key: a.key,
      title: t(`brandVoice.claimArea.${a.key}`, { defaultValue: labelOf(a.categories[0]) }),
      categories: a.categories.filter((c) => isObject(spec[c])),
    })),
    ...unknown.map((c) => ({ key: c, title: c, categories: [c] })),
  ].filter((a) => a.categories.length > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28, fontFamily: FONT_SANS }}>
      {areas.map((area) => {
        const columns = union(area.categories.map((c) => keysOf(spec[c])));
        const grid = [...columns.map(() => 'minmax(0, 1fr)'), ...ACTION_COLUMNS].join(' ');
        const single = area.categories.length === 1;
        return (
          <section key={area.key} aria-label={area.title} style={AREA}>
            {single && <h4 style={TITLE}>{area.title}</h4>}
            {area.categories.map((c) => (
              <ClaimTable
                key={c}
                category={c}
                label={labelOf(c)}
                showLabel={!single}
                columns={columns}
                keys={keysOf(spec[c])}
                required={spec[c].required || []}
                grid={grid}
                value={Array.isArray(current[c]) ? current[c] : []}
                onChange={(rows) => onChange?.({ ...current, [c]: rows })}
                disabled={disabled}
                problems={(problems || []).filter((p) => p?.params?.category === c)}
              />
            ))}
          </section>
        );
      })}
    </div>
  );
}
